import { forwardRef, useState, useEffect } from 'react';
import { Box, Chip, CircularProgress, Alert, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SquarePaymentForm from './SquarePaymentForm';
import StripePaymentForm from './StripePaymentForm';
import { paymentAPI } from '../services/apiService';

/**
 * PaymentForm - Unified Payment Component
 * 
 * Fetches the active payment provider from the SERVER so you can switch
 * providers (Square ↔ Stripe) by changing only the server config —
 * no client app updates or redeployments needed.
 * 
 * GET /api/Payment/Config returns:
 *   { provider: "stripe", publishableKey: "pk_test_..." }
 *   or { provider: "square", appId: "sq0idp-...", locationId: "L..." }
 */
const PaymentForm = forwardRef(({ onTokenGenerated, onTokenCleared, onError }, ref) => {
    const [provider, setProvider] = useState(null);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchConfig = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await paymentAPI.getPaymentConfig();
            setProvider(data.provider);
            setConfig(data);
        } catch (err) {
            console.error('Failed to fetch payment config:', err);
            setError('Could not load payment configuration. Check that the server is running.');
            if (onError) onError('Payment configuration unavailable');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ my: 1 }}>
                <Alert
                    severity="warning"
                    action={
                        <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={fetchConfig}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            {/* Provider indicator */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                <Chip
                    label={provider === 'stripe' ? '💳 Stripe' : '🟦 Square'}
                    size="small"
                    variant="outlined"
                    color={provider === 'stripe' ? 'primary' : 'default'}
                    sx={{ fontSize: '0.7rem' }}
                />
            </Box>

            {provider === 'stripe' ? (
                <StripePaymentForm
                    ref={ref}
                    publishableKey={config.publishableKey}
                    onTokenGenerated={onTokenGenerated}
                    onTokenCleared={onTokenCleared}
                    onError={onError}
                />
            ) : (
                <SquarePaymentForm
                    ref={ref}
                    appId={config.appId}
                    locationId={config.locationId}
                    onTokenGenerated={onTokenGenerated}
                    onTokenCleared={onTokenCleared}
                    onError={onError}
                />
            )}
        </Box>
    );
});

export default PaymentForm;
